import { useState, useCallback, useRef } from 'react'
import { EnterpriseDetail } from '../../../clients/kyousou_api_client'
import {
  getKyousouApiClient,
  ApiErrorResponse,
  handleError,
} from '../../../services/network'
import { useLoading } from '../../../providers/loading_provider'
import { useSnackBar } from '../../../providers/snack_bar_provider'
import { EditFormData } from '../edit/types'

export const useUpdateEnterprise = () => {
  const [isUpdating, setIsUpdating] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const { showLoading, hideLoading } = useLoading()
  const { showSnackBar } = useSnackBar()
  const apiClientRef = useRef(getKyousouApiClient())

  const updateEnterprise = useCallback(
    async (
      kuchikomiEnterpriseId: string,
      editFormData: EditFormData
    ): Promise<EnterpriseDetail | null> => {
      setIsUpdating(true)
      setError(null)
      showLoading()

      try {
        const updateData = {
          kuchikomiEnterpriseId,
          // 基本情報
          name: editFormData.kuchikomi.name,
          description: editFormData.kuchikomi.description,
          delegateLastName: editFormData.kuchikomi.delegateLastName,
          delegateFirstName: editFormData.kuchikomi.delegateFirstName,
          delegatePosition: editFormData.kuchikomi.delegatePosition,
          responsiblePersonName: editFormData.kuchikomi.responsiblePersonName,
          establishDate: editFormData.kuchikomi.establishDate,
          prefecture: editFormData.kuchikomi.prefecture,
          postCode: editFormData.kuchikomi.postCode,
          address: editFormData.kuchikomi.address,
          tel: editFormData.kuchikomi.tel,
          stockCode: editFormData.kuchikomi.stockCode,
          capitalPrice: editFormData.kuchikomi.capitalPrice,
          employeeNum: editFormData.kuchikomi.employeeNum,
          logoUrl: editFormData.kuchikomi.logoUrl,
          officialUrl: editFormData.kuchikomi.officialUrl,
          twitterUrl: editFormData.kuchikomi.twitterUrl,
          facebookUrl: editFormData.kuchikomi.facebookUrl,
          lineUrl: editFormData.kuchikomi.lineUrl,
          tiktokUrl: editFormData.kuchikomi.tiktokUrl,
          industryParentCode: editFormData.kuchikomi.industryParentCode,
          industryChildCode: editFormData.kuchikomi.industryChildCode,
          enterpriseCode: editFormData.kuchikomi.enterpriseCode,
          businessCode: [
            editFormData.kuchikomi.businessCode1,
            editFormData.kuchikomi.businessCode2,
            editFormData.kuchikomi.businessCode3,
          ],
          businessChildCode: [
            editFormData.kuchikomi.businessChildCode1,
            editFormData.kuchikomi.businessChildCode2,
            editFormData.kuchikomi.businessChildCode3,
          ],
          sdgsCategory: editFormData.kuchikomi.interestingSDGs,
          // 企業概要情報
          bannerImageUrl: editFormData.overview?.bannerImageUrl,
          topImages:
            editFormData.overview?.topImages?.map((image) => ({
              id: image.id || null,
              imageUrl: image.imageUrl,
              sortOrder: image.sortOrder,
            })) || [],
          companyProfile: editFormData.overview?.companyProfile || null,
          companyFeature: editFormData.overview?.companyFeature || null,
          providedResource: editFormData.overview?.providedResource || null,
          majorCustomer: editFormData.overview?.majorCustomer || null,
          shareholder: editFormData.overview?.shareholder || null,
          openInnovationAchievements:
            editFormData.overview?.openInnovationAchievements?.map(
              (achievement) => ({
                id: achievement.id || null,
                imageUrl: achievement.imageUrl || null,
                content: achievement.content || null,
              })
            ) || [],
          // 共創情報
          coCreationTitle: editFormData.coCreation?.title || null,
          coCreationContent: editFormData.coCreation?.content || null,
          issue: editFormData.coCreation?.issue || null,
          goal: editFormData.coCreation?.goal || null,
          requirement: editFormData.coCreation?.requirement || null,
          // SDGs取り組み
          renewableEnergy: editFormData.sdgs?.renewableEnergy || null,
          energyEfficiency: editFormData.sdgs?.energyEfficiency || null,
          environmentalEducation:
            editFormData.sdgs?.environmentalEducation || null,
          sdgsInfo: editFormData.sdgs?.sdgsInfo || null,
          // 責任者情報
          departmentName: editFormData.overview?.departmentName || null,
          responsibleLastName:
            editFormData.overview?.responsibleLastName || null,
          responsibleFirstName:
            editFormData.overview?.responsibleFirstName || null,
          responsiblePosition:
            editFormData.overview?.responsiblePosition || null,
          responsibleProfileImg:
            editFormData.overview?.responsibleProfileImg || null,
          responsibleIntroduction:
            editFormData.overview?.responsibleIntroduction || null,
          responsibleMission: editFormData.overview?.responsibleMission || null,
          responsibleComment: editFormData.overview?.responsibleComment || null,
          departmentDescription:
            editFormData.overview?.departmentDescription || null,
          // 探したいタグ
          searchIndustryCategories:
            editFormData.overview?.searchIndustryCategories
              ?.filter(
                (category) =>
                  category.industryParentCode && category.industryChildCode
              )
              .map((category) => ({
                id: category.id || null,
                industryParentCode: category.industryParentCode,
                industryChildCode: category.industryChildCode,
              })) || [],
          // サービス利用アンケート
          companyTypes: editFormData.overview?.companyTypes || null,
          companyStage: editFormData.overview?.companyStage || null,
          servicePurposes: editFormData.overview?.servicePurposes || null,
        }

        const response =
          await apiClientRef.current.enterprise.updateUpdate(updateData)
        showSnackBar(['企業詳細を更新しました'], 'success')
        return response.data as EnterpriseDetail
      } catch (error) {
        console.error('企業詳細更新エラー:', error)
        const errorMessage = '企業詳細の更新に失敗しました'
        setError(errorMessage)
        handleError(error as ApiErrorResponse, showSnackBar)
        return null
      } finally {
        setIsUpdating(false)
        hideLoading()
      }
    },
    [showLoading, hideLoading, showSnackBar]
  )

  return {
    isUpdating,
    error,
    updateEnterprise,
  }
}
